import { Link } from "@remix-run/react";
import type { ActionFunctionArgs } from "@remix-run/node";
import { useState } from "react";
import { PlanType } from "@/routes/app/settings/billing/components/Pricingv2Component";
import { motion } from "framer-motion";
import { TextLogo } from "@/routes/_index/components/Logo";
import { shopTable } from "vitamin-db/drizzle-db/schema/main";
import { drizzleDb } from "vitamin-db";
import { z, ZodError } from "zod";
import { Toaster } from "@/components/ui/toaster";
import { toast } from "@/hooks/use-toast";
import { CheckCheckIcon } from "lucide-react";
import { useFetcherWithPromise } from "@/hooks/useFetcherWithPromise";
import { Button } from "@/components/ui/button";
import { BookALink } from "./BookALink";
import { container, item } from "./BookALink";

const SHOPIFY_DOMAIN = "myshopify.com";
const SHOP_URL_PREFIX = "https://";

// Validate email function
const isValidEmail = (email: string) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

// Validate URL function
const isValidUrl = (url: string) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// Extract shop name from various URL formats
const extractShopName = (input: string): string => {
  // Remove any protocol (http:// or https://)
  let cleaned = input.replace(/^(https?:)?\/\//, "");

  // Remove www. if present
  cleaned = cleaned.replace(/^www\./, "");

  // Remove myshopify.com and any trailing slashes
  cleaned = cleaned.replace(
    new RegExp(`\.${SHOPIFY_DOMAIN.replace(".", "\.")}/?.*$`),
    "",
  );

  return cleaned;
};

// Validate shop name (alphanumeric, hyphens, and underscores only)
const isValidShopName = (name: string): boolean => {
  return /^[a-zA-Z0-9-_]+$/.test(name);
};

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.json();

  try {
    const parsed = z
      .object({
        email: z.string().email(),
        shopUrl: z.string().url(),
        phone: z
          .string()
          .min(8)
          .max(15)
          .refine(
            (val) => {
              // Check if the phone number is in the format +1234567890
              // it could also be +91234567890..
              // remove the spaces, and -
              const cleaned = val.replace(/\s/g, "").replace(/-/g, "");
              console.log(cleaned);
              return /^\+\d{7,15}$/.test(cleaned);
            },
            {
              message:
                "Invalid phone number format. Please use the format +[country code] [local number]",
            },
          ),
      })
      .parse(formData);

    await drizzleDb.insert(shopTable).values({
      id: parsed.shopUrl,
      userEmail: parsed.email,
      currentPlan: PlanType.PRO,
      currentOnboardingStatus: false,
      requestSubmittedAt: new Date().toISOString(),
      shouldUseEmojisInDescription: true,
      shouldUseEmojisInTitle: true,
      phoneNumber: parsed.phone,
    });
  } catch (err) {
    if (
      err instanceof Error &&
      err.message.includes("duplicate key value violates unique constraint")
    ) {
      return {
        error: false,
        message:
          "We've already taken your request, and shall get back to you soon.",
      };
    } else if (err instanceof ZodError) {
      return {
        error: true,
        message: err.issues.map((issue) => issue.message).join(", "),
      };
    }
    return { error: true, message: "Something went wrong" };
  }
  return {
    error: false,
    message:
      "You shall be onboarded soon. Please check your email for the onboarding instructions.",
  };
}

export default function OnboardingRoute() {
  const [error, setError] = useState<string | null>(null);
  const [shopName, setShopName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [currentStep, setCurrentStep] = useState<"1" | "finish">("1");
  const fetcherWithPromise = useFetcherWithPromise<ReturnType<typeof action>>();

  const handleFormSubmissionSuccess = async (payload: {
    email: string;
    shopUrl: string;
    phone: string;
  }) => {
    try {
      const data = await fetcherWithPromise.submit(payload, {
        method: "POST",
        encType: "application/json",
      });

      toast({
        title: data?.message,
        description: data?.message,
        variant: data?.error ? "destructive" : "default",
      });

      if (!data?.error) {
        setCurrentStep("finish");
      }
    } catch (err) {
      console.error("Error checking form submission:", err);
    }
  };

  const handleShopInput = (input: string) => {
    const extractedName = extractShopName(input);
    setShopName(extractedName);
  };

  const handleEmailInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const handlePhoneInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(e.target.value);
  };

  const handleStep1Submit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!shopName || !email) {
      setError("All fields are required");
      return;
    }

    if (!isValidShopName(shopName)) {
      setError(
        "Invalid shop name. Use only letters, numbers, hyphens, and underscores.",
      );
      return;
    }

    if (!isValidEmail(email)) {
      setError("Invalid email address");
      return;
    }

    setError(null);

    const fullShopUrl = `${SHOP_URL_PREFIX}${shopName}.${SHOPIFY_DOMAIN}`;

    const payload = {
      email,
      shopUrl: fullShopUrl,
      phone,
    };

    console.log(payload);

    handleFormSubmissionSuccess(payload);
  };

  const renderStep1 = () => (
    <motion.div
      variants={container}
      initial="hidden"
      animate="show"
      className="mx-auto max-w-2xl"
    >
      <motion.div variants={item} className="text-center">
        <div className="inline-flex items-center gap-2 rounded-full bg-red-50 px-3 py-1 text-sm text-red-600 ring-1 ring-inset ring-red-500/20 mb-3">
          <span className="font-semibold">✨ Welcome</span>
          <span className="h-1 w-1 rounded-full bg-red-500"></span>
          <span>Let's Get Started</span>
        </div>

        <h1 className="mt-4 text-4xl font-bold text-gray-900">
          Set Up Your <span className="text-red-600">Pinterest Automation</span>
        </h1>
        <p className="mt-4 text-lg leading-8 text-gray-600">
          Connect your shop and start growing your Pinterest presence
          automatically
        </p>
      </motion.div>

      <motion.div variants={item}>
        <form onSubmit={handleStep1Submit} className="mt-10 space-y-8">
          <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 space-y-6">
            <div>
              <label
                htmlFor="shopUrl"
                className="block text-sm font-semibold text-gray-900 mb-2"
              >
                Shop Name
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                  <span className="text-gray-500">https://</span>
                </div>
                <input
                  type="text"
                  id="shopUrl"
                  value={shopName}
                  onChange={(e) => handleShopInput(e.target.value)}
                  required
                  className="w-full pl-24 pr-[140px] py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                  placeholder="your-store"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                  <span className="text-gray-500">.myshopify.com</span>
                </div>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Enter your shop name or paste your full Shopify URL
              </p>
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-semibold text-gray-900 mb-2"
              >
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={handleEmailInput}
                required
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-semibold text-gray-900 mb-2"
              >
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                value={phone}
                onChange={handlePhoneInput}
                required
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                placeholder="+1 234 567 890"
              />
            </div>
          </div>

          {/* Preview of full shop URL */}
          {shopName && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-200"
            >
              <span className="font-medium">Your full shop URL:</span>{" "}
              <span className="font-mono">
                {`${SHOP_URL_PREFIX}${shopName}.${SHOPIFY_DOMAIN}`}
              </span>
            </motion.div>
          )}

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500 flex items-center gap-2">
              <svg
                className="h-5 w-5 text-green-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              Your data is secure and private
            </span>

            <button
              type="submit"
              className="inline-flex items-center rounded-md bg-red-600 px-6 py-3 text-base font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 transition-colors"
            >
              Join waitlist
              <svg
                className="ml-2 -mr-1 h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </form>
      </motion.div>

      <motion.div
        variants={item}
        className="mt-10 grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2 text-sm"
      >
        <div className="relative pl-9">
          <dt className="inline font-semibold text-gray-900">
            <svg
              className="absolute left-1 top-1 h-5 w-5 text-red-600"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M5.5 13a3.5 3.5 0 01-.369-6.98 4 4 0 117.753-1.977A4.5 4.5 0 1113.5 13H11V9.413l1.293 1.293a1 1 0 001.414-1.414l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13H5.5z" />
            </svg>
            Quick Setup
          </dt>
          <dd className="inline ml-1 text-gray-600">
            - Connect and start in minutes
          </dd>
        </div>
        <div className="relative pl-9">
          <dt className="inline font-semibold text-gray-900">
            <svg
              className="absolute left-1 top-1 h-5 w-5 text-red-600"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                clipRule="evenodd"
              />
            </svg>
            Automated Posting
          </dt>
          <dd className="inline ml-1 text-gray-600">
            - Let AI handle your Pinterest
          </dd>
        </div>
      </motion.div>
    </motion.div>
  );

  return (
    <div className="relative isolate min-h-screen">
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <nav className="mx-auto flex max-w-7xl items-center justify-center px-8 py-2">
          <Link
            to="/onboarding"
            className="hover:bg-gray-100 p-2 px-4 rounded-md"
          >
            <TextLogo className="text-2xl" />
          </Link>
        </nav>
      </header>

      {/* Background gradient */}
      <motion.div
        className="absolute inset-x-0 top-0 -z-10 transform-gpu overflow-hidden blur-2xl"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="relative aspect-[1155/678] w-[72.1875rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#ff4694] to-[#E60023] opacity-20" />
      </motion.div>

      {/* Main content */}
      <div className="mx-auto px-4 py-12 sm:px-6 lg:px-8">
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto mb-8"
          >
            <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700 text-center border border-red-200">
              {error}
            </div>
          </motion.div>
        )}

        {currentStep === "1" ? renderStep1() : <BookALink />}
      </div>

      <Toaster />
    </div>
  );
}
